// src/pages/ChargeCoinPage.jsx
import React, { useEffect, useState, useContext, useMemo } from "react";
import {
  AppBar, Toolbar, IconButton, Typography, Box, Card, CardContent,
  TextField, Button, Grid, Divider, CircularProgress, Snackbar, Alert,
  CardMedia, Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useParams } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import {
  fetchStores,
  fetchMePopulated,
  pickCoinForStore,
  chargeCoin,
  buildChargeImageUrl,
} from "../api/app/appService";
import getStores from "../api/app/getStroes";
const BASE = process.env.REACT_APP_REACT_APP_CD_API_ENDPOINT;
const PRESETS = [3000, 9000, 30000];

const ChargeCoinPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const auth = useContext(AuthContext);
  const { storeId, userId } = useParams();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [stores, setStores] = useState([]);
  const [me, setMe] = useState(null);

  const [amount, setAmount] = useState("");
  const [errorText, setErrorText] = useState("");

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMsg, setToastMsg] = useState("");
  const [toastType, setToastType] = useState("success"); // success | error

  const selectedStore = useMemo(
    () => stores.find((s) => String(s.id) === String(storeId)),
    [stores, storeId]
  );

  const balance = useMemo(
    () => (me && storeId ? pickCoinForStore(me, storeId) : 0),
    [me, storeId]
  );

  const chargeImageUrl = useMemo(() => {
    if (!selectedStore) return null;
    return buildChargeImageUrl(selectedStore, { baseUrl: BASE });
  }, [selectedStore]);

  const handleBack = () => navigate(-1);

  function validateAmount(v) {
    if (v === "" || v === null) return "請輸入金額";
    const n = Number(v);
    if (!Number.isFinite(n) || !Number.isInteger(n)) return "金額需為整數";
    if (n <= 0) return "金額需大於 0";
    if (n > 1_000_000) return "金額過大";
    return "";
  }

  const handlePreset = (v) => {
    setAmount(String(v));
    setErrorText("");
  };

  const handleAmountChange = (e) => {
    const v = e.target.value.replace(/[^\d]/g, "");
    setAmount(v);
    setErrorText(validateAmount(v));
  };

  async function load() {
    setLoading(true);
    try {
      const [storeList, meData] = await Promise.all([getStores(), fetchMePopulated()]);

      setStores(Array.isArray(storeList.data) ? storeList.data : []);
      setMe(meData);

      const exists = (storeList.data || []).some((s) => String(s.id) === String(storeId));

      if (!exists) {
        setToastType("error");
        setToastMsg("找不到此店家");
        setToastOpen(true);
        // 短暫提示後返回
        setTimeout(() => navigate(-1), 1200);
      }
    } catch (err) {
      setToastType("error");
      setToastMsg("載入資料失敗");
      setToastOpen(true);
    } finally {
      setLoading(false);
    }
  }

function buildPaymentFormHtml({ url, method = "POST", data = {} }) {
  const inputs = Object.entries(data)
    .map(
      ([k, v]) =>
        `<input type="hidden" name="${k}" value="${v == null ? "" : String(v)}" />`
    )
    .join("");
  return `<!doctype html>
<html><head><meta name="viewport" content="width=device-width, initial-scale=1"/></head>
<body>
  <form id="pay" action="${url}" method="${method.toUpperCase()}">
    ${inputs}
  </form>
  <script>document.getElementById('pay').submit();</script>
</body></html>`;
}

async function doCharge() {
  const msg = validateAmount(amount);
  if (msg) { setErrorText(msg); return; }
  if (!storeId) {
    setToastType("error"); setToastMsg("缺少店家參數"); setToastOpen(true);
    return;
  }
  // 直接導航到同分頁橋接頁，保留使用者手勢
  navigate("/pay-bridge", { state: { amount: Number(amount), storeId: String(storeId) } });
}


  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId]);

  if (loading) {
    return (
      <Box width="100%" height="100vh" display="flex" alignItems="center" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box width="100%" minHeight="100vh" sx={{ backgroundColor: theme.palette.background.default }}>
      <AppBar position="static" color="primary">
        <Toolbar>
          <IconButton edge="start" color="inherit" aria-label="back" onClick={handleBack}>
            <ArrowBackIcon />
          </IconButton>
          <Typography alignSelf="center" fontSize={"h6.fontSize"} fontWeight={"bold"}>
            點數儲值
          </Typography>
          <Box sx={{ ml: 2 }}>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              {selectedStore?.name ? `${selectedStore.name}` : ""}
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      <Card sx={{ maxWidth: 560, margin: "24px auto" }}>
  
        <CardContent>
          <Grid container spacing={2}>
            {chargeImageUrl ? (
              <Grid item xs={12}>
                <CardMedia
                  component="img"
                  image={`${process.env.REACT_APP_API_ENDPOINT}/media/${storeId}/member_plan/member_plan.png`}
                  alt="儲值優惠"
                  sx={{ borderRadius: 1,  objectFit: "cover" }}
                />
              </Grid>
            ) : null}

            <Grid item xs={12}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle1">目前點數</Typography>
                <Typography variant="h6" fontWeight="bold">
                  {balance}
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                快速選擇
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                {PRESETS.map((p) => (
                  <Chip
                    key={p}
                    label={`${p}`}
                    onClick={() => handlePreset(p)}
                    clickable
                    variant="outlined"
                  />
                ))}
              </Box>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="儲值金額"
                inputMode="numeric"
                fullWidth
                value={amount}
                onChange={handleAmountChange}
                error={!!errorText}
                helperText={errorText || "輸入欲儲值的點數整數金額"}
              />
            </Grid>

            <Grid item xs={12}>
              <Button
                fullWidth
                size="large"
                variant="contained"
                onClick={doCharge}
                disabled={submitting || !!validateAmount(amount) || !storeId}
              >
                {submitting ? "處理中..." : "確認儲值"}
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Snackbar
        open={toastOpen}
        autoHideDuration={2500}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setToastOpen(false)} severity={toastType} variant="filled" sx={{ width: "100%" }}>
          {toastMsg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ChargeCoinPage;
